<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header hris-page-header-row">
        <div>
          <span class="hris-page-eyebrow">Leaves</span>
          <h1 class="hris-page-title">Employee Leave Credits</h1>
          <p class="hris-page-subtitle">Search employees to review, plot, and modify their leave credits.</p>
        </div>
        <div class="row q-gutter-sm">
          <q-btn unelevated class="hris-q-outline" label="Export all in Excel" no-caps
            @click="extractAllLeave()"></q-btn>
          <q-btn unelevated class="hris-q-primary" label="Export all in CSV" no-caps @click="extractAllLeaveInCsv()"></q-btn>
        </div>
      </div>

      <q-form ref="vForm" @submit="onSubmitSearch" class="hris-toolbar">
        <div class="hris-form-field" style="flex: 1 1 320px; max-width: 420px;">
          <q-input outlined dense v-model="searchModel" placeholder="Search employee here..." color="green">
            <template v-slot:append>
              <q-icon name="fa fa-search"></q-icon>
            </template>
          </q-input>
        </div>
      </q-form>

      <div class="hris-panel">
        <div class="hris-panel-title">Search Result</div>
        <q-table flat title="" :data="searchResultData" :columns="computedSearchColumns" row-key="credit_id"
          :hide-bottom="searchResultData.length < 5" :loading="loading">
          <q-tr slot="body" slot-scope="props" :props="props" class="cursor-pointer"
            @click="searchRowClick(props.row.name, props.row.credit_id, props.row.employee_id, props.row.max_vl_credits, props.row.date_created, props.row.company_id, props.row.is_unified)">
            <q-td key="employee_id" :props="props">{{ props.row.employee_id }}</q-td>
            <q-td key="name" :props="props">{{ props.row.name }}</q-td>
            <q-td key="position" :props="props">{{ props.row.position }}</q-td>
            <q-td key="department" :props="props">{{ props.row.department }}</q-td>
            <template v-if="props.row.is_unified">
              <q-td key="unified_credits" :props="props" class="text-center text-bold">
                {{ props.row.vl_credits }} / {{ (parseFloat(props.row.max_vl_credits) + parseFloat(props.row.max_sl_credits)).toFixed(2) }}
              </q-td>
            </template>
            <template v-else>
              <q-td key="vl_credits" :props="props">{{ props.row.vl_credits }} / {{ props.row.max_vl_credits }}</q-td>
              <q-td key="sl_credits" :props="props">{{ props.row.sl_credits }} / {{ props.row.max_sl_credits }}</q-td>
            </template>
            <q-td key="date_created" :props="props">{{ props.row.date_created }}</q-td>
          </q-tr>
          <template v-slot:no-data="{ message }">
            <div class="hris-empty full-width">{{ message }}</div>
          </template>
        </q-table>
      </div>

      <div class="hris-panel" v-if="empForModify">
        <div class="hris-form-section-title">Leave Modification / Details For: {{ empName }}</div>
          <div class="hris-form-row">
            <div class="hris-form-field" style="flex-basis: 48%;">
              <label class="hris-form-label">{{ is_pto_enabled ? 'Add Max Leave Credits' : 'Add Max VL/SL Credits' }}</label>
              <q-slider v-model="added_vlsl_maxcredits" :min="0" :max="limited_vlsl_credits" :step="0.5" snap
                markers label label-always color="green">
              </q-slider>
            </div>
            <div class="hris-form-field" style="flex-basis: 24%;">
              <q-input class="q-ma-none mod-input" outlined dense v-model="form.modifyReason" label="Reason For Modification"
                clearable type="textarea" color="green"></q-input>
            </div>
            <div class="hris-form-field" style="flex-basis: 22%;">
              <div class="column justify-between q-gutter-xs" style="height: 100%">
                <q-btn unelevated class="hris-q-outline" @click="extractIndividualLeave" label="Extract Details" no-caps></q-btn>
                <q-btn unelevated class="hris-q-primary" :loading="lvEffectLoading" @click="confirmModifyLeave" label="Confirm Update" no-caps>
                  <template v-slot:loading>
                    <q-spinner color="green" class="on-left"></q-spinner>
                    Updating
                  </template>
                </q-btn>
              </div>
            </div>
          </div>
          <div class="hris-form-row" style="margin-top: 8px;">
            <div class="hris-form-field" style="flex-basis: 100%;">
              <div class="row justify-between items-center q-mb-sm">
                <div class="hris-panel-title" style="margin: 0;">Leave Spread (Monthly Accruals)</div>
                <div class="q-gutter-xs row items-center">
                  <q-toggle v-model="manualPlotMode" label="Manual Plot Mode" color="green" dense></q-toggle>
                  <q-btn v-if="manualPlotMode" unelevated class="hris-q-primary" label="Save Manual Plot" dense no-caps
                    @click="saveManualPlot"></q-btn>
                </div>
              </div>
              <q-table flat class="center-cell" :data="leaveSpreadData" :columns="leaveSpreadColumns" row-key="name"
                hide-bottom separator="cell">
                <q-tr slot="body" slot-scope="props" :props="props">
                  <q-td key="year" :props="props">{{ props.row.year }}</q-td>
                  <q-td v-for="(earnedItem, index) in props.row.earned" :key="index">
                    <div v-if="!manualPlotMode || (earnedItem.vl === '' && earnedItem.sl === '')">
                      <template v-if="is_pto_enabled">
                        {{ (parseFloat(earnedItem.vl || 0) + parseFloat(earnedItem.sl || 0)).toFixed(2) }}
                      </template>
                      <template v-else>
                        {{ earnedItem.vl }} / {{ earnedItem.sl }}
                      </template>
                    </div>
                    <div v-else class="column q-gutter-xs">
                      <template v-if="is_pto_enabled">
                        <q-input v-model="manualPlotForm[index].vl" type="number" step="0.01" dense outlined label="Unified"
                          color="green" font-size="11px" input-class="text-center" style="min-width: 60px;"></q-input>
                      </template>
                      <template v-else>
                        <q-input v-model="manualPlotForm[index].vl" type="number" step="0.01" dense outlined label="VL"
                          color="green" input-class="text-center" style="min-width: 60px;"></q-input>
                        <q-input v-model="manualPlotForm[index].sl" type="number" step="0.01" dense outlined label="SL"
                          color="green" input-class="text-center" style="min-width: 60px;"></q-input>
                      </template>
                    </div>
                  </q-td>
                  <q-td key="total" :props="props">{{ props.row.total }}</q-td>
                </q-tr>
              </q-table>
            </div>
          </div>
          <div class="hris-form-row" style="margin-top: 8px;">
            <div class="hris-form-field" style="flex-basis: 100%;">
                <template v-if="is_pto_enabled">
                  <div class="hris-grid q-mb-sm">
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Remaining Leave Credits</p>
                      <p class="hris-stat-value">{{ vl_remaining }}</p>
                    </div>
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Earned Leave Credits</p>
                      <p class="hris-stat-value">{{ (parseFloat(vl_earned) + parseFloat(sl_earned)).toFixed(2) }}</p>
                    </div>
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Availed Leave Credits</p>
                      <p class="hris-stat-value">{{ (parseFloat(vl_availed) + parseFloat(sl_availed)).toFixed(2) }}</p>
                    </div>
                  </div>
                </template>
                <template v-else-if="is_pto_enabled === false">
                  <div class="hris-grid q-mb-sm">
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Max VL Credits</p>
                      <p class="hris-stat-value">{{ max_vl }}</p>
                    </div>
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Max SL Credits</p>
                      <p class="hris-stat-value">{{ max_sl }}</p>
                    </div>
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Earned VL</p>
                      <p class="hris-stat-value">{{ vl_earned }}</p>
                    </div>
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Earned SL</p>
                      <p class="hris-stat-value">{{ sl_earned }}</p>
                    </div>
                  </div>
                  <div class="hris-grid q-mb-sm">
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Availed VL</p>
                      <p class="hris-stat-value">{{ vl_availed }}</p>
                    </div>
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Availed SL</p>
                      <p class="hris-stat-value">{{ sl_availed }}</p>
                    </div>
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Remaining VL</p>
                      <p class="hris-stat-value">{{ vl_remaining }}</p>
                    </div>
                    <div class="hris-stat-card">
                      <p class="hris-stat-label">Remaining SL</p>
                      <p class="hris-stat-value">{{ sl_remaining }}</p>
                    </div>
                  </div>
                </template>
                <div class="hris-grid">
                  <div class="hris-stat-card row items-center q-gutter-sm">
                    <q-avatar :icon="ml_availment" font-size="16px" color="grey-3" :text-color="ml_avail_color"></q-avatar>
                    <p class="hris-stat-label" style="margin: 0;">Availed ML</p>
                  </div>
                  <div class="hris-stat-card row items-center q-gutter-sm">
                    <q-avatar :icon="pl_availment" font-size="16px" color="grey-3" :text-color="pl_avail_color"></q-avatar>
                    <p class="hris-stat-label" style="margin: 0;">Availed PL</p>
                  </div>
                  <div class="hris-stat-card row items-center q-gutter-sm">
                    <q-avatar :icon="mc_availment" font-size="16px" color="grey-3" :text-color="mc_avail_color"></q-avatar>
                    <p class="hris-stat-label" style="margin: 0;">Availed MC</p>
                  </div>
                  <div class="hris-stat-card row items-center q-gutter-sm">
                    <q-avatar :icon="sp_availment" font-size="16px" color="grey-3" :text-color="sp_avail_color"></q-avatar>
                    <p class="hris-stat-label" style="margin: 0;">Availed SP</p>
                  </div>
                </div>
            </div>
          </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import { date } from 'quasar';
import { exportFile } from 'quasar';
import { mapGetters } from 'vuex';
let today = Date.now();
let dateToday = date.formatDate(today, 'MM-DD-YYYY-hhmmA');
export default {
  name: 'EmployeeLeaveCredits',
  data() {
    return {
      dateToday,
      empName: '',
      empForModify: false,
      loading: false,
      lvEffectLoading: false,
      plottingLoading: false,
      searchModel: null,
      searchResultData: [],
      searchResultColumns: [{
        name: 'employee_id',
        field: 'employee_id',
        label: 'Employee ID',
        style: 'width: 160px',
        align: 'center',
        headerStyle: 'width: 160px'
      },
      {
        name: 'name',
        field: 'name',
        label: 'Name',
        style: 'width: 250px',
        align: 'center',
        headerStyle: 'width: 250px'
      },
      {
        name: 'position',
        field: 'position',
        label: 'Position',
        style: 'width: 250px',
        align: 'center',
        headerStyle: 'width: 250px'
      },
      {
        name: 'department',
        field: 'department',
        label: 'Department',
        align: 'center',
      },
      {
        name: 'vl_credits',
        field: 'vl_credits',
        label: 'Vacation Credits',
        style: 'width: 250px',
        align: 'center',
        headerStyle: 'width: 250px'
      },
      {
        name: 'sl_credits',
        field: 'sl_credits',
        label: 'Sick Credits',
        style: 'width: 250px',
        align: 'center',
        headerStyle: 'width: 250px'
      },
      {
        name: 'date_created',
        field: 'date_created',
        label: 'Date Credit',
        style: 'width: 250px',
        align: 'center',
        headerStyle: 'width: 250px'
      }
      ],
      current_max_vlsl_credits: 0,
      limited_vlsl_credits: 0,
      added_vlsl_maxcredits: 0,
      selectedEmployeeID: null,
      max_vl: null,
      max_sl: null,
      vl_earned: null,
      sl_earned: null,
      vl_availed: null,
      sl_availed: null,
      vl_remaining: null,
      sl_remaining: null,
      ml_availed: null,
      ml_availment: 'fa fa-times',
      ml_avail_color: 'red-10',
      pl_availed: null,
      pl_availment: 'fa fa-times',
      pl_avail_color: 'red-10',
      mc_availed: null,
      mc_availment: 'fa fa-times',
      mc_avail_color: 'red-10',
      sp_availed: null,
      sp_availment: 'fa fa-times',
      sp_avail_color: 'red-10',
      leaveSpreadData: [],
      leaveSpreadColumns: [{
        name: 'year',
        field: 'year',
        label: 'Year',
        align: 'center',
        style: 'font-weight: bold; width: 100px',
        classes: 'bg-grey-2',
        headerClasses: 'bg-blue-8 text-white text-center',
        headerStyle: 'width: 100px'
      },
      {
        name: 'january',
        field: 'january',
        label: 'Jan',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'february',
        field: 'february',
        label: 'Feb',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'march',
        field: 'march',
        label: 'Mar',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'april',
        field: 'april',
        label: 'Apr',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'may',
        field: 'may',
        label: 'May',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'june',
        field: 'june',
        label: 'Jun',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'july',
        field: 'july',
        label: 'Jul',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'august',
        field: 'august',
        label: 'Aug',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'september',
        field: 'september',
        label: 'Sep',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'october',
        field: 'october',
        label: 'Oct',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'november',
        field: 'november',
        label: 'Nov',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'december',
        field: 'december',
        label: 'Dec',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      },
      {
        name: 'total',
        field: 'total',
        label: 'Total',
        align: 'center',
        style: 'width: 80px',
        headerClasses: 'bg-blue-8 text-white',
        headerStyle: 'width: 80px'
      }
      ],
      form: {
        modifiedMaxVLSLCredits: 0,
        addedMaxVLSLCredits: 0,
        modifyReason: null,
        selectedCreditID: null,
        currentMaxVLSLCredits: 0
      },
      manualPlotMode: false,
      manualPlotForm: [],
      employeeCompanyId: null,
      selectedDateCreated: null,
      
      adminPlotDialog: false,
      leaveOptions: [
        'Emergency Leave',
        'Vacation Leave',
        'Sick Leave',
        'Maternity Leave',
        'Paternity Leave'
      ],
    }
  },

  computed: {
    ...mapGetters({
        userData: 'auth/getUser'
    }),
    is_pto_enabled() {
      return (this.userData?.features || []).includes('pto');
    },
    computedSearchColumns: function() {
      if (this.is_pto_enabled) {
        return this.searchResultColumns.filter(c => c.name !== 'vl_credits' && c.name !== 'sl_credits').concat([{
          name: 'unified_credits',
          label: 'Leave Credits',
          align: 'center',
          style: 'width: 500px',
          headerStyle: 'width: 500px'
        }]);
      }
      return this.searchResultColumns;
    }
  },

  created() {
    this.onSubmitSearch()
  },

  methods: {
    onSubmitSearch: function () {
      this.empName = '';
      this.empForModify = false;
      this.loading = true;

      axios.create({
        baseURL: this.apiUrl,
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      })
        .get('/leave/credits/search-employee-credits', {
          params: {
            keyword: this.searchModel
          }
        })
        .then((res) => {
          this.loading = false;
          this.searchResultData = [];
          for (let emp of res.data.data) {
            this.searchResultData.push({
              credit_id: emp.credit_id,
              employee_id: emp.employee_id,
              name: this.toTitleCase(emp.name),
              position: emp.position,
              department: emp.department,
              vl_credits: emp.vl_credits,
              sl_credits: emp.sl_credits,
              max_vl_credits: emp.max_vl_credits,
              max_sl_credits: emp.max_sl_credits,
              is_unified: emp.is_unified || false,
              date_created: emp.date_created,
              company_id: emp.company_id
            });
          }
        })
    },
    searchRowClick: function (rowName, rowID, empID, maxVL, dateCreated, companyId, isUnified) {
      this.added_vlsl_maxcredits = 0;
      this.ml_availment = 'fa fa-times';
      this.ml_avail_color = 'red-10';
      this.pl_availment = 'fa fa-times';
      this.pl_avail_color = 'red-10';
      this.mc_availment = 'fa fa-times';
      this.mc_avail_color = 'red-10';
      this.sp_availment = 'fa fa-times';
      this.sp_avail_color = 'red-10';
      this.empName = rowName;
      this.form.selectedCreditID = rowID;
      this.selectedEmployeeID = empID;
      this.empForModify = true;
      this.limited_vlsl_credits = 15 - maxVL;
      this.current_max_vlsl_credits = maxVL;
      this.employeeCompanyId = companyId;
      this.selectedDateCreated = dateCreated;

      axios.create({
        baseURL: this.apiUrl,
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      })
        .get('/leave/credits/retrieve', {
          params: {
            category: 'all',
            keyword: rowID,
            date_created: dateCreated
          }
        })
        .then((res) => {
          let leaveCredits = res.data.data;
          this.max_vl = leaveCredits.max_vl;
          this.max_sl = leaveCredits.max_sl;
          this.vl_earned = leaveCredits.vl_earned;
          this.sl_earned = leaveCredits.sl_earned;
          this.vl_availed = leaveCredits.vl_availed;
          this.sl_availed = leaveCredits.sl_availed;
          this.vl_remaining = leaveCredits.vl_remaining;
          this.sl_remaining = leaveCredits.sl_remaining;
          this.ml_availed = leaveCredits.ml_availed;
          this.pl_availed = leaveCredits.pl_availed;
          this.mc_availed = leaveCredits.mc_availed;
          this.sp_availed = leaveCredits.sp_availed;
          this.leaveSpreadData = leaveCredits.plotted;

          this.manualPlotForm = [];
          if (leaveCredits.plotted && leaveCredits.plotted.length > 0) {
            leaveCredits.plotted[0].earned.forEach((monthData) => {
              const vlVal = parseFloat(monthData.vl || 0);
              const slVal = parseFloat(monthData.sl || 0);

              this.manualPlotForm.push({
                month: monthData.month + 1,
                vl: this.is_pto_enabled ? (vlVal + slVal) : vlVal,
                sl: this.is_pto_enabled ? 0 : slVal
              });
            });
          }

          if (this.ml_availed > 0) {
            this.ml_availment = 'fa fa-check';
            this.ml_avail_color = 'green-10';
          }
          if (this.pl_availed > 0) {
            this.pl_availment = 'fa fa-check';
            this.pl_avail_color = 'green-10';
          }
          if (this.mc_availed > 0) {
            this.mc_availment = 'fa fa-check';
            this.mc_avail_color = 'green-10';
          }
          if (this.sp_availed > 6) {
            this.sp_availment = 'fa fa-check';
            this.sp_avail_color = 'green-10';
          }
        });
    },
    confirmModifyLeave: function () {
      if (this.added_vlsl_maxcredits == 0) {
        this.$q.notify({
          color: 'red-6',
          textColor: 'white',
          icon: 'fa fa-exclamation-circle',
          message: 'No modification done.'
        });
      } else if (this.form.modifyReason == null) {
        this.$q.notify({
          color: 'red-6',
          textColor: 'white',
          icon: 'fa fa-exclamation-circle',
          message: 'Please indicate reason for modification.'
        });
      } else {
        this.lvEffectLoading = true;
        this.form.addedMaxVLSLCredits = Number(this.added_vlsl_maxcredits);
        this.form.currentMaxVLSLCredits = Number(this.current_max_vlsl_credits);
        this.form.modifiedMaxVLSLCredits = Number(this.added_vlsl_maxcredits) + Number(this.current_max_vlsl_credits);
        let formData = new FormData();
        formData.append('form_data', JSON.stringify(this.form));
        axios.create({
          baseURL: this.apiUrl,
          headers: {
            'Authorization': `Bearer ${this.token}`
          }
        })
          .post('/leave/credits/modify-credits', formData)
          .then((res) => {
            setTimeout(() => {
              this.lvEffectLoading = false;
              this.$q.notify({
                color: 'green-7',
                textColor: 'white',
                icon: 'fa fa-edit',
                message: 'Leave Credits Modified Successfully'
              });
              this.onSubmitSearch();
              this.form.modifiedMaxVLSLCredits = 0;
              this.form.addedMaxVLSLCredits = 0;
              this.form.modifyReason = null;
              this.form.selectedCreditID = null;
              this.added_vlsl_maxcredits = 0;
            }, 1000);
          });
      }
    },
    saveManualPlot: function () {
      if (!this.leaveSpreadData || this.leaveSpreadData.length === 0) {
        this.$q.notify({ color: "negative", message: "No leave spread data available." });
        return;
      }
      
      const payload = {
        employee_id: this.selectedEmployeeID,
        company_id: this.employeeCompanyId,
        year: this.leaveSpreadData[0].year || new Date().getFullYear(),
        plots: this.manualPlotForm.map(p => ({
          month: p.month,
          vl: this.is_pto_enabled ? p.vl : p.vl,
          sl: this.is_pto_enabled ? 0 : p.sl
        }))
      };

      axios.create({
        baseURL: this.apiUrl,
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      })
        .post('/leave/credits/save-manual-plots', payload)
        .then(res => {
          if (res.data.status) {
            this.$q.notify({ color: "positive", message: res.data.message });
            this.searchRowClick(this.empName, this.form.selectedCreditID, this.selectedEmployeeID, this.current_max_vlsl_credits, this.selectedDateCreated, this.employeeCompanyId, this.is_pto_enabled);
            this.manualPlotMode = false;
          }
        });
    },
    extractIndividualLeave: function () {
      axios.create({
        baseURL: this.apiUrl,
        responseType: 'blob',
        headers: { 'Authorization': `Bearer ${this.token}` }
      })
        .get('/leave/credits/exportIndividual', {
          params: {
            keyword: this.form.selectedCreditID
          }
        })
        .then((res) => {
          var fileURL = window.URL.createObjectURL(new Blob([res.data]));
          var fileLink = document.createElement('a');
          fileLink.href = fileURL;
          fileLink.setAttribute('download', this.selectedEmployeeID + '-' + this.removeFullnameSpace(this.empName) + '-Leave-Credits_AO_' + this.dateToday + '.xlsx');
          document.body.appendChild(fileLink);
          fileLink.click();
        });
    },
    extractAllLeave: function () {
      axios.create({
        baseURL: this.apiUrl,
        responseType: 'blob',
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).get(`/leave/credits/exportAll`)
        .then((res) => {
          var fileURL = window.URL.createObjectURL(new Blob([res.data]));
          var fileLink = document.createElement('a');
          fileLink.href = fileURL;
          fileLink.setAttribute('download', 'All-Leave-Credits_AO_' + this.dateToday + '.xlsx');
          document.body.appendChild(fileLink);
          fileLink.click();
        });
    },

    extractAllLeaveInCsv: function () {
      axios.create({
        baseURL: this.apiUrl,
        responseType: 'blob',
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).get(`/leave/credits/exportAllInCsv`)
        .then((res) => {
          var fileURL = window.URL.createObjectURL(new Blob([res.data]));
          var fileLink = document.createElement('a');
          fileLink.href = fileURL;
          fileLink.setAttribute('download', 'All-Leave-Credits_AO_' + this.dateToday + '.csv');
          document.body.appendChild(fileLink);
          fileLink.click();
        });
    }
  }
}
</script>
<style lang="css" scoped>
.text-height {
  font-size: 30px;
  text-align: center;
}

.mod-input {
  height: 122px;
  min-height: 122px;
  resize: none;
  overflow: hidden;
}

.center-cell td {
  text-align: center;
}
</style>
